<template>
  <div>
    <div v-if="loading" class="flex justify-center py-10">
      <Loader />
    </div>

    <div v-else-if="survey">
      <div class="max-h-[60vh] overflow-y-auto pr-2 space-y-6">
        <div
          v-for="section in survey.sections"
          :key="`section-${section.id}`"
        >
          <strong v-if="section.title">{{ section.title }}</strong>
          <div
            v-for="question in section.questions"
            :key="`question-${question.id}`"
            class="py-3"
          >
            <p class="font-medium mb-2">
              {{ question.title }}
            </p>
            <p v-if="question.description" class="text-sm text-slate-500 mb-2">
              {{ question.description }}
            </p>
            <SurveyBuiltAnswer
              :survey-id="survey.id"
              :question="question"
              editable
              density="compact"
              :value="answers[question.id]"
              @change="(value) => onAnswer(question.id, value)"
            />
          </div>
        </div>
      </div>

      <div class="shadow-[0_-6px_6px_-6px_rgba(0,0,0,0.2)] mx-4 pt-2">
        <BaseButton
          class="w-full"
          :disabled="!allAnswered"
          :loading="submitting"
          primary
          @click="submit"
        >
          Valider
        </BaseButton>
      </div>
    </div>

    <div v-else class="text-center text-slate-500 py-10">
      Questionnaire introuvable.
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {
    ActionComponentProps,
    SurveyFlowchartNode,
    SurveyResolvedAction
} from "@/core/interfaces/flowchart/flowchart";
import {Survey} from "@/core/interfaces/survey/Survey";
import {surveyApi} from "@/container";
import SurveyBuiltAnswer from "@/survey/components/SurveyBuiltAnswer.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Loader from "@/core/components/base/spinner/Loader.vue";

const props = defineProps<ActionComponentProps<SurveyFlowchartNode>>();
const emits = defineEmits<{
    (e: 'resolved', payload: SurveyResolvedAction): void;
}>();

const survey = ref<Survey | null>(null);
const loading = ref<boolean>(true);
const submitting = ref<boolean>(false);

/** Answers keyed by question id; each value is the array expected by the answer API. */
const answers = ref<Record<number, (string | number | boolean)[]>>({});

/** The flat list of questions across all sections. */
const questions = computed(() => (survey.value?.sections ?? []).flatMap(section => section.questions ?? []));

/** Whether every (non-optional) question has an answer. */
const allAnswered = computed(() => {
    return questions.value.every(question => question.optional || (answers.value[question.id]?.length ?? 0) > 0);
});

/**
 * Resolve the survey from its slug, then load its full built structure.
 */
onMounted(async () => {
    const slug = props.node.action_payload.survey_slug;

    if (!slug) {
        loading.value = false;

        return;
    }

    try {
        survey.value = await surveyApi().readBuiltSurveyBySlug(slug);
    } finally {
        loading.value = false;
    }
});

/**
 * Register an answer for a question.
 *
 * @param questionId The question id.
 * @param value The value emitted by the answer component.
 */
const onAnswer = (questionId: number, value: unknown) => {
    answers.value[questionId] = Array.isArray(value) ? value : [value as string | number | boolean];
};

/**
 * Submit every answer at once, fetch the verdict, and emit it for the parent to route.
 */
const submit = async () => {
    if (!survey.value) {
        return;
    }

    submitting.value = true;

    try {
        const instance = await surveyApi().createInstance(survey.value.id, props.consumer.consumer_key);

        const formattedAnswers = Object.entries(answers.value).map(([questionId, value]) => ({
            question_id: Number(questionId),
            value: value,
        }));

        const response = await surveyApi().answerAll(survey.value.id, instance.id, formattedAnswers, new Date().toISOString());

        const verdict = response.verdict;

        emits('resolved', {
            type: 'survey',
            node: props.node,
            verdict: verdict ? {
                slug: verdict.flowchart_slug ?? verdict.slug,
                title: verdict.title,
                score: verdict.score,
                instance_id: instance.id,
            } : null,
        });
    } finally {
        submitting.value = false;
    }
};
</script>
